import _ from 'lodash'
import dayjs, { Dayjs } from 'dayjs'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useResizeDetector } from 'react-resize-detector'

import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import Availabilities from '@/components/ui/calendar/Availabilities'
import DatePickerInput from '@/components/ui/inputs/DatePickerInput'
import DoctorIcon from '@/components/ui/icons/Doctor'
import SelectInput from '@/components/ui/inputs/SelectInput'
import TimePickerInput from '@/components/ui/inputs/TimePickerInput'

import { Slot, WorkingHours } from '@/core/models/schedule.model'

import { useDateTimePickerOptions } from '@/hooks/useDateTimePickerOptions'
import { useLanguage } from '@/hooks/useLanguage'

import { adapterLocaleMap } from '@/utils/locale'
import { generateSlots } from '@/utils/slots'

export type StaffAvailabilityProps = {
  busySlots: Slot[]
  calendsoAppointmentTypes: Array<{
    id: number
    type: string
    duration: number
  }>
  defaultStartDate?: Date
  isSelected?: boolean
  onCalendarViewChange?: (dateFrom: Date, dateTo: Date, staff: StaffAvailabilityProps['staff']) => void
  onChangeStartDate?: (startDate: Date | null, duration?: number, appointmentTypeId?: number, consultationReason?: string) => void
  onSelectStaff?: (staffId: string, startDate: Date | null) => void
  selectable?: boolean
  staff: {
    id: string
    email: string
    name: string
    speciality: string | undefined
    status: string
    surname: string
  }
  workingHours: WorkingHours[]
  timeZone?: string
  showOnlyAvailability?: boolean
}

function breakEmail(email: string) {
  if (email.length > 15) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Tooltip title={email}>
          <Typography variant="body2" sx={{ mr: 1 }}>
            {`${email.slice(0, 15)}...`}
          </Typography>
        </Tooltip>
      </Box>
    )
  } else {
    return <Typography variant="body2">{email}</Typography>
  }
}

export function StaffAvailability(props: StaffAvailabilityProps) {
  const {
    calendsoAppointmentTypes,
    busySlots,
    defaultStartDate,
    isSelected = false,
    onCalendarViewChange,
    onChangeStartDate,
    onSelectStaff,
    selectable = true,
    staff,
    workingHours,
    timeZone,
    showOnlyAvailability = false,
  } = props
  const language = useLanguage()
  const { t, i18n } = useTranslation()
  const { ref: containerRef, width: containerWidth } = useResizeDetector()

  const { currentDateTime, datePickerValue, setDatePickerValue, timePickerOptions, timePickerValue, setTimePickerValue } =
    useDateTimePickerOptions(defaultStartDate)

  const [currentCalendarViewStartDate, setCurrentCalendarViewStartDate] = useState<Date>(defaultStartDate ?? new Date())
  const [selectedCalendsoAppointmentType, setSelectedCalendsoAppointmentType] = useState<
    StaffAvailabilityProps['calendsoAppointmentTypes'][number] | undefined
  >(calendsoAppointmentTypes[0])
  const [selectedSlot, setSelectedSlot] = useState<string | undefined>(defaultStartDate?.toISOString())
  const [consultationStartDate, setConsultationStartDate] = useState<Date | null>(defaultStartDate ?? new Date())
  const [selectedConsultationReason, setSelectedConsultationReason] = useState<string>()

  const calendsoAppointmentTypeOptions = useMemo(
    () =>
      _.map(calendsoAppointmentTypes, (appointmentType) => ({
        label: appointmentType.type,
        value: appointmentType.id,
      })),
    [calendsoAppointmentTypes]
  )

  const availabilities = useMemo(() => {
    return _.range(1, 8).map((dayOfWeek) => ({
      date: dayjs(currentCalendarViewStartDate).isoWeekday(dayOfWeek).format('DD/MM/YYYY'),
      slots: generateSlots(
        dayjs(currentCalendarViewStartDate).isoWeekday(dayOfWeek).toDate(),
        workingHours.filter((workingHour) => (selectedConsultationReason && workingHour.kind ? workingHour.kind === selectedConsultationReason : true)),
        busySlots,
        selectedCalendsoAppointmentType?.duration ?? 0,
        timeZone
      ),
    }))
  }, [busySlots, currentCalendarViewStartDate, selectedCalendsoAppointmentType, selectedConsultationReason, workingHours, timeZone])

  // only our schedule availabilities have kind in working hour, calendso has not
  const consultationReasonOptions = useMemo(
    () =>
      _.uniq(_.compact(workingHours.map((workingHour) => workingHour.kind))).map((kind) => ({
        label: kind,
        value: kind,
      })),
    [workingHours]
  )

  // check if availability is set or not
  const isAvailabilitySet = useMemo(() => {
    if (!workingHours.length) return false

    if (workingHours.length === 1) {
      const { days, startTime, endTime } = workingHours[0]
      return !(_.isEqual(days, _.range(7)) && startTime === 0 && endTime === 24 * 60 - 1)
    }

    return true
  }, [workingHours])

  useEffect(() => {
    dayjs.locale(i18n.language)
  }, [i18n.language])

  useEffect(() => {
    if (!!selectedConsultationReason || !consultationReasonOptions.length) return

    setSelectedConsultationReason(consultationReasonOptions[0].value)
  }, [consultationReasonOptions, selectedConsultationReason])

  useEffect(() => {
    if (!selectedCalendsoAppointmentType && !!calendsoAppointmentTypes.length) {
      setSelectedCalendsoAppointmentType(calendsoAppointmentTypes[0])
    }
  }, [calendsoAppointmentTypes, selectedCalendsoAppointmentType])

  const handleChangeAppointmentType = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedCalendsoAppointmentType(_.find(calendsoAppointmentTypes, ['id', Number(event.target.value)]))
  }

  const handleCalendarViewChange = useCallback(
    (fromDate: Date, toDate: Date) => {
      setCurrentCalendarViewStartDate(fromDate)
      onCalendarViewChange?.(fromDate, toDate, staff)
    },
    [staff, onCalendarViewChange]
  )

  const handleChangeConsultationStartDate = (date: Date | null) => {
    setConsultationStartDate(date)
    onChangeStartDate?.(date)
  }

  const handleChangeDatePickerValue = (date: Dayjs | null) => {
    setDatePickerValue(date)
    const startDate =
      date && timePickerValue && dayjs(date).isValid() && dayjs(timePickerValue).isValid()
        ? dayjs(date).set('hour', timePickerValue.get('hour')).set('minute', timePickerValue.get('minute')).toDate()
        : null
    handleChangeConsultationStartDate(startDate)
  }

  const handleChangeTimePickerValue = (time: Dayjs | null) => {
    setTimePickerValue(time)
    const startDate =
      datePickerValue && time && dayjs(datePickerValue).isValid() && dayjs(time).isValid()
        ? dayjs(datePickerValue).set('hour', time.get('hour')).set('minute', time.get('minute')).toDate()
        : null
    handleChangeConsultationStartDate(startDate)
  }

  const handleSelectStaff = () => {
    if (!isSelected) {
      onSelectStaff?.(
        staff.id,
        isAvailabilitySet ? (selectedSlot && dayjs(selectedSlot).isValid() ? dayjs(selectedSlot).toDate() : null) : consultationStartDate || null
      )
    }
  }

  const handleSelectSlot = useCallback(
    (start: Date, end: Date) => {
      setSelectedSlot(start.toISOString())
      onChangeStartDate?.(start, dayjs(end).diff(start, 'minutes'), selectedCalendsoAppointmentType?.id, selectedConsultationReason)
    },
    [availabilities, onChangeStartDate, selectedCalendsoAppointmentType]
  )

  let error = !isAvailabilitySet && isSelected && (consultationStartDate ? dayjs(consultationStartDate).isBefore(currentDateTime, 'minutes') : false)

  return (
    <Paper
      data-testid={`${staff.status === 'MEDECIN' ? 'doctor' : 'pds'}-availability-${staff.id}`}
      ref={containerRef}
      onClick={handleSelectStaff}
      sx={[
        {
          p: '10px 16px',
          position: 'relative',
        },
        isSelected && {
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'primary.main',
          boxShadow: '0 2px 14px #3333',
        },
      ]}
    >
      {!isSelected && selectable && <Box sx={{ cursor: 'pointer', position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1 }} />}
      <Grid container spacing={1}>
        <Grid item xs={12} sm={4} lg={3}>
          <Stack spacing={1} direction="column" alignItems="center" justifyContent="start">
            <DoctorIcon fontSize="large" />
            <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>{_.trim(`${staff.name || ''} ${staff.surname || ''}`)}</Typography>
            {breakEmail(staff.email)}
            {staff.speciality && <Chip color="info" label={staff.speciality} size="small" variant="filled" sx={{ fontSize: '12px' }} />}
            {isAvailabilitySet && consultationReasonOptions.length > 0 && !selectedCalendsoAppointmentType && (
              <SelectInput
                onChange={(event) => {
                  setSelectedConsultationReason(event.target.value)
                }}
                options={consultationReasonOptions}
                value={selectedConsultationReason ?? ''}
              />
            )}
            {isAvailabilitySet && !consultationReasonOptions.length && selectedCalendsoAppointmentType && (
              <SelectInput onChange={handleChangeAppointmentType} options={calendsoAppointmentTypeOptions} value={selectedCalendsoAppointmentType.id} />
            )}
          </Stack>
        </Grid>
        <Grid item xs={12} sm={8} lg={9}>
          {isAvailabilitySet || showOnlyAvailability ? (
            <Availabilities
              availabilities={availabilities}
              defaultShowAllSlots={!!defaultStartDate}
              onNext={handleCalendarViewChange}
              onPrevious={handleCalendarViewChange}
              onSelectSlot={handleSelectSlot}
              selectedSlot={selectedSlot}
              startFrom={defaultStartDate ? dayjs(defaultStartDate).startOf('week').toDate() : undefined}
            />
          ) : (
            <Box>
              <Grid container spacing={2}>
                <Grid item fit-content="true">
                  <Typography
                    variant={'h6'}
                    sx={[
                      {
                        color: (theme) => theme.palette.common.black,
                        fontSize: '14px',
                      },
                    ]}
                  >
                    {t('page.createConsultation.information.noCalCom.label')}
                  </Typography>
                </Grid>
                <Grid item fit-content="true">
                  <Grid container spacing={2} direction="row">
                    <Grid item xs={6}>
                      <DatePickerInput
                        disablePast
                        error={error}
                        helperText={error && t('yup:date.minDate', { min: currentDateTime.format('DD/MM/YYYY HH[h]mm') })}
                        value={datePickerValue}
                        onChange={handleChangeDatePickerValue}
                      />
                    </Grid>
                    <Grid item xs={6} sx={{ height: '100%' }}>
                      <TimePickerInput
                        error={error}
                        value={timePickerValue}
                        onChange={handleChangeTimePickerValue}
                        options={timePickerOptions}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          )}
        </Grid>
      </Grid>
    </Paper>
  )
}

export default StaffAvailability
